import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { setSearchOpen } from 'app/providers/uiSlice';
import { Overlay } from 'components/ui/Overlay';
import { useTypingPlaceholder } from 'schools/parashari/hooks/useTypingPlaceholder';
import { useJyotishaLanguage } from 'schools/parashari/components/jyotisha/JyotishaLanguageContext';
import { TOPIC_SEARCH_OPEN_EVENT, type TopicSearchOpenDetail } from 'schools/parashari/config/searchSections';
import { searchGlobalTopics } from 'shared/search/globalTopicSearch';
import {
  buildGlobalSearchResultHref,
  dispatchParashariTopicOpen,
  getParashariInitialPage,
} from 'shared/search/navigateToSearchResult';
import {
  pickSchoolSearchLabel,
  SEARCHABLE_SCHOOL_SLUGS,
} from 'shared/search/schoolSearchConfig';
import {
  GLOBAL_SEARCH_TYPING_EXAMPLES_EN,
  GLOBAL_SEARCH_TYPING_EXAMPLES_HI,
} from 'shared/search/searchPlaceholders';
import type { GlobalSearchFilter, GlobalSearchResult } from 'shared/search/types';
import { cn } from 'utils/cn';

function resultKey(result: GlobalSearchResult): string {
  return `${result.schoolSlug}:${result.parashariSection ?? 'core'}:${result.topicId}`;
}

export function GlobalTopicSearchModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const reduxOpen = useAppSelector((s) => s.ui.searchOpen);
  const { showEnglish } = useJyotishaLanguage();

  const [eventOpen, setEventOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const [query, setQuery] = useState('');
  const [schoolFilter, setSchoolFilter] = useState<GlobalSearchFilter>('all');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [typingRestart, setTypingRestart] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputId = useId();
  const listId = useId();

  const open = reduxOpen || eventOpen;

  const close = useCallback(() => {
    dispatch(setSearchOpen(false));
    setEventOpen(false);
    setQuery('');
    setInitialQuery('');
    setActiveIndex(-1);
  }, [dispatch]);

  const openWithDetail = useCallback((detail: TopicSearchOpenDetail = {}) => {
    const nextQuery = detail.initialQuery ?? '';
    setInitialQuery(nextQuery);
    setQuery(nextQuery);
    setSchoolFilter('all');
    setActiveIndex(-1);
    setEventOpen(true);
    dispatch(setSearchOpen(true));
  }, [dispatch]);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<TopicSearchOpenDetail>).detail ?? {};
      openWithDetail(detail);
    };
    window.addEventListener(TOPIC_SEARCH_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(TOPIC_SEARCH_OPEN_EVENT, onOpen);
  }, [openWithDetail]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const onShortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (open) {
          close();
        } else {
          dispatch(setSearchOpen(true));
        }
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [open, close, dispatch]);

  useEffect(() => {
    if (open) setQuery(initialQuery);
  }, [initialQuery, open]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const legacyQuery = params.get('q')?.trim();
    if (!legacyQuery) return;
    openWithDetail({ initialQuery: legacyQuery });
    params.delete('q');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.state, navigate, openWithDetail]);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const showTypingAnimation = query.length === 0 && !prefersReducedMotion;
  const typingText = useTypingPlaceholder({
    enabled: showTypingAnimation,
    showEnglish,
    restartKey: typingRestart,
    phrasesEn: [...GLOBAL_SEARCH_TYPING_EXAMPLES_EN],
    phrasesHi: [...GLOBAL_SEARCH_TYPING_EXAMPLES_HI],
  });

  const results = useMemo(
    () => searchGlobalTopics(query, schoolFilter),
    [query, schoolFilter],
  );

  useEffect(() => {
    setActiveIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  const handleSelect = useCallback(
    (result: GlobalSearchResult) => {
      const href = buildGlobalSearchResultHref(result);
      const searchQuery = query.trim();
      close();
      const initialPage = getParashariInitialPage(result);
      navigate(href, initialPage ? { state: { initialPage } } : undefined);
      dispatchParashariTopicOpen(result, searchQuery);
    },
    [close, navigate, query],
  );

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((idx) => (idx + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((idx) => (idx <= 0 ? results.length - 1 : idx - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      const picked = results[activeIndex];
      if (picked) handleSelect(picked);
    }
  };

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLElement>(`[data-result-index="${activeIndex}"]`);
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const panelShell = 'border-border bg-surface-secondary shadow-modal';

  const chipBase = 'shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50';
  const chipIdle =
    'border border-border bg-surface text-content-secondary hover:border-gold/40 hover:bg-surface-secondary hover:text-content';
  const chipActive =
    'border border-gold/50 bg-gold/15 text-accent-strong shadow-subtle';

  const showResults = query.trim().length >= 2;

  if (!open) return null;

  return (
    <Overlay
      open={open}
      onClose={close}
      align="center"
      zIndexClass="z-[var(--z-modal)]"
      backdropClassName="bg-black/60 backdrop-blur-md"
      dialogClassName="global-topic-search-dialog w-full max-w-2xl"
      aria-label={showEnglish ? 'Search topics across Takshify' : 'Takshify में विषय खोजें'}
    >
      <div
        className={cn(
          'global-topic-search-panel overflow-hidden rounded-2xl border p-4 shadow-2xl sm:p-5',
          panelShell,
        )}
      >
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-headline text-content">
                {showEnglish ? 'Search topics' : 'विषय खोजें'}
              </p>
              <p className="mt-0.5 text-caption text-content-secondary">
                {showEnglish
                  ? 'Syllabus lessons across all active schools'
                  : 'सभी सक्रिय स्कूलों के पाठ्यक्रम विषय'}
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="nav-icon-btn shrink-0"
              aria-label={showEnglish ? 'Close search' : 'खोज बंद करें'}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div
            className="flex gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label={showEnglish ? 'Filter by school' : 'स्कूल से फ़िल्टर करें'}
          >
            <button
              type="button"
              role="tab"
              aria-selected={schoolFilter === 'all'}
              className={cn(chipBase, schoolFilter === 'all' ? chipActive : chipIdle)}
              onClick={() => setSchoolFilter('all')}
            >
              {showEnglish ? 'All schools' : 'सभी स्कूल'}
            </button>
            {SEARCHABLE_SCHOOL_SLUGS.map((slug) => (
              <button
                key={slug}
                type="button"
                role="tab"
                aria-selected={schoolFilter === slug}
                className={cn(chipBase, schoolFilter === slug ? chipActive : chipIdle)}
                onClick={() => setSchoolFilter(slug)}
              >
                {pickSchoolSearchLabel(slug, showEnglish, true)}
              </button>
            ))}
          </div>
        </div>

        <label
          htmlFor={inputId}
          className={cn(
            'global-topic-search-field flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 transition-colors',
            'focus-within:border-gold/50 focus-within:bg-surface-secondary focus-within:shadow-subtle',
          )}
        >
          <Search className="h-5 w-5 shrink-0 text-content-tertiary" aria-hidden />
          <div className="relative min-w-0 flex-1">
            {showTypingAnimation ? (
              <span
                className={cn(
                  'pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap text-base text-content-secondary',
                  !showEnglish && 'font-devanagari',
                )}
                lang={showEnglish ? 'en' : 'hi'}
                aria-hidden
              >
                <span className="truncate">{typingText}</span>
                <span className="search-typing-cursor ml-px inline-block h-[1em] w-px shrink-0 align-[-2px]" />
              </span>
            ) : null}
            <input
              ref={inputRef}
              id={inputId}
              type="search"
              value={query}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={
                showTypingAnimation
                  ? ''
                  : showEnglish
                    ? 'Type a topic, concept, or Sanskrit term…'
                    : 'विषय, अवधारणा या संस्कृत शब्द लिखें…'
              }
              aria-label={showEnglish ? 'Search topics' : 'विषय खोजें'}
              aria-controls={showResults ? listId : undefined}
              aria-activedescendant={
                showResults && activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined
              }
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => {
                if (query.length === 0) setTypingRestart((k) => k + 1);
              }}
              onKeyDown={handleInputKeyDown}
              className={cn(
                'w-full min-w-0 bg-transparent text-base text-content outline-none placeholder:text-content-tertiary',
                !showEnglish && 'font-devanagari',
              )}
            />
          </div>
          {query.length > 0 ? (
            <button
              type="button"
              onClick={close}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-content-tertiary transition-colors hover:bg-surface-secondary hover:text-content"
              aria-label={showEnglish ? 'Close search' : 'खोज बंद करें'}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </label>

        {showResults ? (
          <div
            id={listId}
            role="listbox"
            className="global-topic-search-results mt-3 max-h-[min(52dvh,24rem)] overflow-y-auto overscroll-contain rounded-xl border border-border bg-surface"
          >
            {results.length === 0 ? (
              <p
                className={cn(
                  'px-4 py-4 text-sm text-content-secondary',
                  !showEnglish && 'font-devanagari',
                )}
                lang={showEnglish ? 'en' : 'hi'}
              >
                {showEnglish
                  ? 'No topics found. Try another term or switch the school filter.'
                  : 'कोई विषय नहीं मिला। दूसरा शब्द आज़माएँ या स्कूल फ़िल्टर बदलें।'}
              </p>
            ) : (
              <ul ref={listRef} className="p-1.5">
                {results.map((result, index) => {
                  const title = showEnglish ? result.titleEn : result.titleHi;
                  const group = showEnglish ? result.groupLabelEn : result.groupLabelHi;
                  const schoolLabel = pickSchoolSearchLabel(result.schoolSlug, showEnglish, true);
                  const selected = index === activeIndex;

                  return (
                    <li key={resultKey(result)} role="presentation">
                      <button
                        type="button"
                        role="option"
                        id={`${listId}-option-${index}`}
                        data-result-index={index}
                        aria-selected={selected}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left outline-none transition-colors',
                          selected
                            ? 'bg-gold/15 ring-1 ring-gold/30'
                            : 'hover:bg-surface-secondary',
                        )}
                      >
                        <span className="pt-0.5 text-lg leading-none" aria-hidden>
                          {result.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span
                              className={cn(
                                'text-sm font-semibold text-content',
                                !showEnglish && 'font-devanagari',
                              )}
                              lang={showEnglish ? 'en' : 'hi'}
                            >
                              {title}
                            </span>
                            <span className="rounded-md bg-gold/15 px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wide text-gold">
                              {schoolLabel}
                            </span>
                          </span>
                          <span
                            className={cn(
                              'mt-0.5 block text-[11px] font-medium text-content-secondary',
                              !showEnglish && 'font-devanagari',
                            )}
                            lang={showEnglish ? 'en' : 'hi'}
                          >
                            {group}
                          </span>
                          {result.matchSnippet ? (
                            <span className="mt-1 block truncate text-[11px] text-content-tertiary">
                              …{result.matchSnippet}…
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <p className="mt-3 px-1 text-caption text-content-tertiary">
            {showEnglish
              ? 'Type at least 2 characters. Use school chips above to narrow results.'
              : 'कम से कम २ अक्षर लिखें। ऊपर स्कूल चिप से परिणाम सीमित करें।'}
          </p>
        )}

        <p className="mt-3 hidden text-center text-[11px] text-content-tertiary sm:block">
          <kbd className="rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-2xs">
            ⌘K
          </kbd>{' '}
          {showEnglish ? 'to open anywhere' : 'कहीं भी खोलने के लिए'}
        </p>
      </div>
    </Overlay>
  );
}

export default GlobalTopicSearchModal;
